import React, { useCallback, useEffect, useMemo, useState } from "react";
import { withRouter } from "react-router-dom";
import {
  Row,
  Col,
  Spinner,
  Badge,
  Progress,
  Alert,
  Table,
} from "reactstrap";
import axios from "axios";
import ApexCharts from "react-apexcharts";
import Widget from "../../components/Widget/Widget.js";
import Am4chartMap from "../../components/am4chartMap/am4chartMap";
import {
  API_TIMEOUT,
  NIRLABA_DASHBOARD_PROGRAM_DETAIL,
} from "../../utils/index.js";
import { getMitraQueryParams } from "../../utils/adminMitra";
import { numberWithCommas } from "../../utils/func.js";
import "eva-icons/style/eva-icons.css";
import dashboardStyles from "../dashboard/Dashboard.module.scss";
import mapTableS from "../../components/am4chartMap/am4chartMap.module.scss";
import s from "../tables/BantuanProgressDetailPage.module.scss";

const formatRupiah = (n) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const DESIL_BAR_COLORS = [
  "#16365f",
  "#1a5084",
  "#41d5e2",
  "#ffc405",
  "#43bc13",
  "#94a3b8",
];

const APEX_FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const NIK_MASK_LAST = 8;
const maskNikTampilan = (nik) => {
  const raw = nik != null ? String(nik).replace(/\s/g, "") : "";
  if (!raw) return "—";
  const x = "x".repeat(NIK_MASK_LAST);
  if (raw.length <= NIK_MASK_LAST) return x;
  return raw.slice(0, raw.length - NIK_MASK_LAST) + x;
};

const toSmallCaps = (t) =>
  (t || "")
    .toString()
    .toLowerCase()
    .replace(/(?:^|\s)\S/g, (c) => c.toUpperCase());

const labelDesilDtsen = (desil) => {
  if (desil == null || desil === undefined || desil === "") return "—";
  const n = Number(desil);
  if (n === 0) return "Belum ada (DTSEN)";
  if (n >= 1 && n <= 5) return `Desil ${n}`;
  return `> Desil 5 (${n})`;
};

function NirlabaBantuanDetailPage({ location, history }) {
  const { tahunQ, bantuanQ, lembagaQ } = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return {
      tahunQ: (params.get("tahun") || "").trim(),
      bantuanQ: params.get("bantuan") || "",
      lembagaQ: (params.get("id_lembaga") || "").trim(),
    };
  }, [location.search]);

  const bantuanDecoded = useMemo(() => {
    try {
      return decodeURIComponent(bantuanQ);
    } catch {
      return bantuanQ;
    }
  }, [bantuanQ]);

  const [detail, setDetail] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchDetail = useCallback(() => {
    if (!tahunQ || !bantuanDecoded) {
      setLoading(false);
      setDetail(null);
      setLoadError("Parameter tahun atau program tidak lengkap.");
      return;
    }
    setLoading(true);
    setLoadError("");
    const mitra = getMitraQueryParams();
    axios
      .get(NIRLABA_DASHBOARD_PROGRAM_DETAIL, {
        timeout: API_TIMEOUT,
        params: {
          tahun: tahunQ,
          bantuan: bantuanDecoded,
          id_lembaga: lembagaQ || mitra.id_lembaga || undefined,
        },
      })
      .then((res) => {
        if (res.data?.success !== 1 || !res.data?.data) {
          setDetail(null);
          setLoadError("Data program mitra tidak ditemukan untuk tahun ini.");
          return;
        }
        setDetail(res.data.data);
      })
      .catch(() => {
        setDetail(null);
        setLoadError("Gagal menghubungi server.");
      })
      .finally(() => setLoading(false));
  }, [tahunQ, bantuanDecoded, lembagaQ]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const penerimaSorted = useMemo(() => {
    const rows = (detail?.penerima || []).filter(
      (r) => r.nama != null && String(r.nama).trim() !== ""
    );
    return [...rows].sort((a, b) => {
      const k = String(a.kecamatan || "").localeCompare(
        String(b.kecamatan || ""),
        "id"
      );
      if (k !== 0) return k;
      const d = String(a.desa || "").localeCompare(String(b.desa || ""), "id");
      if (d !== 0) return d;
      return String(a.nama || "").localeCompare(String(b.nama || ""), "id");
    });
  }, [detail]);

  const riwayatSeries = detail?.riwayat || [];

  const riwayatLimaTahunChart = useMemo(() => {
    if (!riwayatSeries.length) return null;
    const cats = riwayatSeries.map((d) => String(d.tahun));
    const penerima = riwayatSeries.map((d) => Number(d.jumlah_penerima) || 0);
    return {
      series: [{ name: "Total penerima (hasil input)", data: penerima }],
      options: {
        chart: {
          type: "line",
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: APEX_FONT_FAMILY,
        },
        stroke: { width: 3, curve: "smooth" },
        colors: ["#1976d2"],
        markers: { size: 4, strokeWidth: 2 },
        legend: { position: "top", horizontalAlign: "left", fontSize: "13px" },
        xaxis: {
          categories: cats,
          labels: { style: { colors: "#6b7280" } },
          axisBorder: { show: false },
        },
        yaxis: {
          min: 0,
          title: {
            text: "Jumlah (orang)",
            style: { color: "#6b7280", fontSize: "12px" },
          },
          labels: {
            formatter: (val) =>
              Math.round(Number(val) || 0).toLocaleString("id-ID"),
            style: { colors: "#6b7280" },
          },
        },
        grid: {
          borderColor: "#f1f3f5",
          strokeDashArray: 4,
        },
      },
    };
  }, [riwayatSeries]);

  const desilRows = useMemo(() => {
    if (!detail) return [];
    const d1 = Number(detail.totalDesil1) || 0;
    const d2 = Number(detail.totalDesil2) || 0;
    const d3 = Number(detail.totalDesil3) || 0;
    const d4 = Number(detail.totalDesil4) || 0;
    const d5 = Number(detail.totalDesil5) || 0;
    const above5 = Number(detail.totalDesilAbove5) || 0;
    return [
      { key: "d1", label: "Desil 1", short: "D1", count: d1 },
      { key: "d2", label: "Desil 2", short: "D2", count: d2 },
      { key: "d3", label: "Desil 3", short: "D3", count: d3 },
      { key: "d4", label: "Desil 4", short: "D4", count: d4 },
      { key: "d5", label: "Desil 5", short: "D5", count: d5 },
      { key: "above5", label: "Di atas D5", short: "> D5", count: above5 },
    ];
  }, [detail]);

  const desilChart = useMemo(() => {
    const categories = desilRows.map((r) => r.short);
    const data = desilRows.map((r) => r.count);
    const totalIn = detail ? Number(detail.jumlahpenerima) || 0 : 0;
    return {
      series: [{ name: "Penerima", data }],
      options: {
        chart: {
          type: "bar",
          toolbar: { show: false },
          fontFamily: APEX_FONT_FAMILY,
          zoom: { enabled: false },
        },
        plotOptions: {
          bar: {
            borderRadius: 6,
            columnWidth: "62%",
            distributed: true,
            dataLabels: { position: "top" },
          },
        },
        colors: DESIL_BAR_COLORS,
        dataLabels: {
          enabled: true,
          offsetY: -18,
          style: { fontSize: "11px", colors: ["#304758"], fontWeight: 600 },
          formatter: (val) =>
            val > 0 ? Number(val).toLocaleString("id-ID") : "",
        },
        legend: { show: false },
        xaxis: {
          categories,
          labels: { style: { colors: "#6b7280", fontSize: "12px" } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          title: {
            text: "Jumlah penerima",
            style: { color: "#6b7280", fontSize: "12px" },
          },
          labels: {
            formatter: (val) => Math.round(val).toLocaleString("id-ID"),
            style: { colors: "#6b7280" },
          },
        },
        grid: { borderColor: "#f1f3f5", strokeDashArray: 4 },
        tooltip: {
          y: {
            formatter: (val) => {
              const pct =
                totalIn > 0 ? ((Number(val) / totalIn) * 100).toFixed(1) : "0";
              return `${val.toLocaleString("id-ID")} orang (${pct}% dari terinput)`;
            },
          },
        },
      },
    };
  }, [desilRows, detail]);

  const pctInput =
    detail && detail.jmlPenerima
      ? Math.round(
          (Number(detail.jumlahpenerima) / Number(detail.jmlPenerima)) * 100
        )
      : 0;
  const pctInputTarget = Math.min(100, Math.max(0, pctInput));
  const totalPenerimaIn = detail ? Number(detail.jumlahpenerima) || 0 : 0;
  const totalDesil12 = detail
    ? (Number(detail.totalDesil1) || 0) + (Number(detail.totalDesil2) || 0)
    : 0;
  const pctDesil12 =
    totalPenerimaIn > 0
      ? Math.round((totalDesil12 / totalPenerimaIn) * 100)
      : 0;
  const pctDesil12Bar = Math.min(100, Math.max(0, pctDesil12));

  const d = dashboardStyles;

  return (
    <div className={s.page}>
      <button
        type="button"
        className={s.backLink}
        onClick={() => history.push("/admin/nirlaba/program")}
      >
        <span className={s.backIcon} aria-hidden="true">
          ←
        </span>
        Program mitra
      </button>

      {loading && (
        <div className={s.loader}>
          <Spinner color="secondary" size="sm" />
          <span>Memuat detail program mitra…</span>
        </div>
      )}

      {!loading && loadError && (
        <div className={s.errorBox} role="alert">
          {loadError}
        </div>
      )}

      {!loading && detail && (
        <>
          <div className={d.summaryRekapanCard}>
            <div className={`${d.summaryRekapanHeader} ${s.detailRekapanHeader}`}>
              <h4 className={`${d.summaryRekapanTitle} ${s.detailRekapanTitle}`}>
                {detail.bantuan}
              </h4>
              <div className={s.headerChips}>
                <span className={s.metaChip}>Tahun {detail.tahun}</span>
                {detail.nama_lembaga ? (
                  <span className={s.metaChipMuted}>{detail.nama_lembaga}</span>
                ) : null}
                {detail.sumber_dana ? (
                  <span className={s.metaChipMuted}>{detail.sumber_dana}</span>
                ) : null}
              </div>
            </div>

            <div className={d.summaryRekapanGrid}>
              <div className={`${d.summaryItem} ${d.summaryItemRed}`}>
                <span className={d.summaryIconWrap}>
                  <i className="eva eva-checkmark-circle-outline eva-lg" />
                </span>
                <div className={d.summaryContent}>
                  <span className={d.summaryLabel}>Kuota program</span>
                  <span className={d.summaryValue}>
                    {numberWithCommas(detail.jmlPenerima)}
                  </span>
                </div>
              </div>
              <div
                className={`${d.summaryItem} ${d.summaryItemHighlight} ${d.summaryItemBlue} ${s.summaryItemAlignStart}`}
              >
                <span className={`${d.summaryIconWrap} ${s.iconAlignTop}`}>
                  <i className="eva eva-person-done-outline eva-lg" />
                </span>
                <div className={`${d.summaryContent} ${s.terinputContent}`}>
                  <span className={d.summaryLabel}>Terinput</span>
                  <span className={d.summaryValue}>
                    {numberWithCommas(detail.jumlahpenerima)}
                    {detail.jmlPenerima ? (
                      <span className={s.pctInline}> ({pctInput}%)</span>
                    ) : null}
                  </span>
                  {detail.jmlPenerima ? (
                    <Progress
                      value={pctInputTarget}
                      className={s.cardProgressBar}
                      color={pctInput >= 50 ? "success" : "warning"}
                    />
                  ) : null}
                </div>
              </div>
              <div className={`${d.summaryItem} ${d.summaryItemBlue}`}>
                <span className={d.summaryIconWrap}>
                  <i className="eva eva-credit-card-outline eva-lg" />
                </span>
                <div className={d.summaryContent}>
                  <span className={d.summaryLabel}>Total nilai</span>
                  <span className={d.summaryValue}>
                    {formatRupiah(detail.totalUang)}
                  </span>
                </div>
              </div>
              <div
                className={`${d.summaryItem} ${d.summaryItemGreen} ${s.summaryItemAlignStart}`}
              >
                <span className={`${d.summaryIconWrap} ${s.iconAlignTop}`}>
                  <i className="eva eva-heart-outline eva-lg" />
                </span>
                <div className={`${d.summaryContent} ${s.terinputContent}`}>
                  <span className={d.summaryLabel}>Desil 1 &amp; 2</span>
                  <span className={d.summaryValue}>
                    {numberWithCommas(totalDesil12)}
                    <span className={s.pctInline}> ({pctDesil12}%)</span>
                  </span>
                  <Progress
                    value={pctDesil12Bar}
                    className={s.cardProgressBar}
                    color={pctDesil12 >= 70 ? "success" : "info"}
                  />
                </div>
              </div>
            </div>

            {(detail.bentuk || detail.target) && (
              <div className={s.docRow}>
                {detail.bentuk ? (
                  <Badge className={s.docBadge}>Bentuk {detail.bentuk}</Badge>
                ) : null}
                {detail.target ? (
                  <Badge className={s.docBadge}>Target {detail.target}</Badge>
                ) : null}
              </div>
            )}
          </div>

          <Row className={`gutter mb-4 ${s.mainRow}`}>
            <Col xs={12}>
              <Widget
                style={{ padding: 20 }}
                className={`${dashboardStyles.chartPenerimaDesil} ${s.chartWidget}`}
              >
                <div className={s.chartTitle}>Penerima bantuan per desil</div>
                <p className={s.chartCaption}>
                  Grafik batang membandingkan jumlah penerima mitra tiap
                  kategori desil (sumber DTSEN, hanya yang sudah terinput).
                </p>
                <div className={s.chartMount}>
                  <ApexCharts
                    key={`${tahunQ}-${detail.bantuan}`}
                    options={desilChart.options}
                    series={desilChart.series}
                    type="bar"
                    height={320}
                  />
                </div>
              </Widget>
            </Col>
          </Row>

          <Row className={`gutter mb-4 ${s.mainRow}`}>
            <Col xs={12}>
              <Widget
                style={{ padding: 20 }}
                className={`${dashboardStyles.chartPenerimaDesil} ${s.chartWidget} ${s.riwayatWidget}`}
              >
                <div className={s.chartTitle}>
                  Program yang sama — lima tahun terakhir
                </div>
                <p className={s.chartCaption}>
                  Tren jumlah penerima program mitra dengan nama yang sama.
                </p>
                {riwayatLimaTahunChart ? (
                  <div className={s.chartMount}>
                    <ApexCharts
                      key={`riwayat-mitra-${tahunQ}-${detail.bantuan}`}
                      options={riwayatLimaTahunChart.options}
                      series={riwayatLimaTahunChart.series}
                      type="line"
                      height={320}
                    />
                  </div>
                ) : (
                  <Alert color="secondary" className="mb-0 py-2 small">
                    Belum ada riwayat.
                  </Alert>
                )}
              </Widget>
            </Col>
          </Row>

          <Row className={`gutter ${s.mainRow}`}>
            <Col xs={12}>
              <Widget style={{ padding: 20 }} className={s.mapWidget}>
                <div className={`${s.chartTitle} ${s.mapSectionTitle}`}>
                  Sebaran per desa
                </div>
                <Am4chartMap
                  tahun={String(tahunQ)}
                  fixedBantuan={detail.bantuan}
                  source="mitra"
                  lembagaId={detail.id_lembaga}
                  compactHeader
                  hidePerDesaTable
                />
                <div className={mapTableS.mapDataTableWrap}>
                  <h6 className={mapTableS.mapDataTableTitle}>
                    Daftar nama penerima mitra — Tahun {detail.tahun}
                  </h6>
                  {penerimaSorted.length === 0 && (
                    <div className={s.mapTableMessage}>
                      Belum ada penerima dengan nama terisi.
                    </div>
                  )}
                  {penerimaSorted.length > 0 && (
                    <div className={mapTableS.mapDataTableScroll}>
                      <Table
                        responsive
                        bordered
                        size="sm"
                        className={mapTableS.mapDataTable}
                      >
                        <thead>
                          <tr>
                            <th className={mapTableS.thNo}>No</th>
                            <th>Nama penerima</th>
                            <th>NIK</th>
                            <th>Desa</th>
                            <th>Kecamatan</th>
                            <th>Desil (DTSEN)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {penerimaSorted.map((row, idx) => (
                            <tr
                              key={
                                row.id != null
                                  ? `pn-${row.id}`
                                  : `pn-${row.nik}-${idx}`
                              }
                            >
                              <td className={mapTableS.thNo}>{idx + 1}</td>
                              <td>{toSmallCaps(row.nama)}</td>
                              <td className={mapTableS.thNum}>
                                {maskNikTampilan(row.nik)}
                              </td>
                              <td>{toSmallCaps(row.desa)}</td>
                              <td>{toSmallCaps(row.kecamatan)}</td>
                              <td className={mapTableS.thNum}>
                                {labelDesilDtsen(row.desil_nasional)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </Widget>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}

export default withRouter(NirlabaBantuanDetailPage);
